import { Suspense, lazy, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';

import { authApi } from './api/auth';
import { ApiError } from './api/client';
import { useAuthStore } from './state/authStore';

// Eager: everything a stranger can reach without signing in. These are the routes where the first
// paint is the product — the landing page, the calculator someone tries before handing over an
// email address, and the two auth forms. Deferring them would trade a smaller download for a blank
// frame on exactly the pages that have to look immediate.
import { marketingLanguageForPath } from './components/landing/LandingShell';
import { TermHints } from './components/TermHints';
import { LandingPage, PricingPage, PrivacyPage, TermsPage } from './pages/MarketingPages';
// Eager for a reason of their own. The server writes the article into the page before any script
// runs; a lazy route renders nothing while its chunk loads, so React would wipe that article and
// put it back a moment later. These two are small, and they are what a search visitor lands on.
import AboutPage from './pages/AboutPage';
import CookiesPage from './pages/CookiesPage';
import ConsentBanner from './components/ConsentBanner';
import BlogIndexPage from './pages/blog/BlogIndexPage';
import BlogPostPage from './pages/blog/BlogPostPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import NotFoundPage from './pages/NotFoundPage';
import { setLanguage } from './i18n';

/*
 * Lazy: the signed-in app.
 *
 * It was all one chunk, so a visitor who came for the landing page downloaded the invoice editor,
 * the ЗДДС panels, the securities import and the filing walkthrough before anything rendered —
 * pages they cannot open without an account, on the one view where a slow first paint costs a
 * signup. Splitting at the route boundary is the whole win; a signed-in user pays a few hundred
 * milliseconds once, on a page they navigated to deliberately.
 */
/* Lazy, unlike the landing pages beside it. The calculator is reached by a click rather than
   landed on, so a round trip costs nobody their first paint — and it is the largest public page. */
const CalculatorPage = lazy(() => import('./pages/CalculatorPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const IncomePage = lazy(() => import('./pages/IncomePage'));
const InvoicesPage = lazy(() => import('./pages/InvoicesPage'));
const StocksPage = lazy(() => import('./pages/StocksPage'));
const VatPage = lazy(() => import('./pages/VatPage'));
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
const DeclarationsPage = lazy(() => import('./pages/DeclarationsPage'));
const BillingPage = lazy(() => import('./pages/BillingPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const HelpPage = lazy(() => import('./pages/HelpPage'));
const UnsubscribePage = lazy(() => import('./pages/UnsubscribePage'));

// Reached from an email link, so it is lazy for the same reason the app is: nobody navigates here
// from inside the product.
const PasswordPages = {
  ConfirmEmail: lazy(() =>
    import('./pages/PasswordPages').then((m) => ({ default: m.ConfirmEmailPage })),
  ),
  ForgotPassword: lazy(() =>
    import('./pages/PasswordPages').then((m) => ({ default: m.ForgotPasswordPage })),
  ),
  ResetPassword: lazy(() =>
    import('./pages/PasswordPages').then((m) => ({ default: m.ResetPasswordPage })),
  ),
};

function RequireAuth({ children }: { children: React.ReactElement }) {
  const { t } = useTranslation();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const resolved = useAuthStore((s) => s.resolved);

  // Redirecting before the session is known would bounce a signed-in user to the login page on
  // every hard refresh.
  if (!resolved) {
    return <p className="page muted">{t('common.loading')}</p>;
  }

  if (!user) {
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnUrl=${returnUrl}`} replace />;
  }

  return children;
}

export default function App() {
  const setUser = useAuthStore((s) => s.setUser);
  const markResolved = useAuthStore((s) => s.markResolved);
  const { i18n } = useTranslation();

  useEffect(() => {
    let cancelled = false;

    authApi
      .me()
      .then((user) => {
        if (cancelled) {
          return;
        }

        setUser(user);

        // The account's stored language wins over whatever the browser guessed — but not over the
        // URL. A marketing path names its own language, ships a pre-rendered head in it and declares
        // an hreflang pair; overriding that from the account meant a signed-in Bulgarian user who
        // followed a link to /en/terms read Bulgarian under an English URL and an English <title>.
        // Which language a marketing URL stands for is decided in exactly one place, and this is
        // not it.
        if (marketingLanguageForPath(window.location.pathname) !== null) {
          return;
        }

        const preferred = user.language === 'En' ? 'en' : 'bg';
        if (i18n.resolvedLanguage !== preferred) {
          void setLanguage(preferred);
        }
      })
      .catch((error: unknown) => {
        // A 401 here is the ordinary "nobody is signed in" case, not a failure.
        if (!cancelled && error instanceof ApiError && error.status === 401) {
          setUser(null);
        } else if (!cancelled) {
          markResolved();
        }
      });

    return () => {
      cancelled = true;
    };
  }, [setUser, markResolved, i18n]);

  return (
    <>
    {/* Beside the routes rather than inside one: it explains abbreviations on every page, the
        public ones most of all. */}
    <TermHints />
    {/* Also beside the routes: it watches every navigation, asks the cookie question on the public
        site, and keeps third-party scripts out of the signed-in one. Renders nothing unless a tag
        is configured. See lib/tracking.ts. */}
    <ConsentBanner />
    {/*
     * One boundary around the whole route table rather than one per route. The fallback is
     * deliberately empty: a chunk on a local network resolves in a few milliseconds, and a spinner
     * that appears and vanishes inside one frame reads as a flicker — worse than nothing happening
     * at all. On a slow connection the previous page simply stays until the next is ready, which is
     * what a native app does.
     */}
    <Suspense fallback={null}>
      <Routes>
        {/* Bulgarian at the root, English under /en — the pre-rendered heads and hreflang pairs
            in scripts/generate-seo-pages.mjs depend on these exact paths. */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/en" element={<LandingPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/en/pricing" element={<PricingPage />} />
        <Route path="/calculator" element={<CalculatorPage />} />
        <Route path="/en/calculator" element={<CalculatorPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/en/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/en/terms" element={<TermsPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/en/about" element={<AboutPage />} />
        <Route path="/cookies" element={<CookiesPage />} />
        <Route path="/en/cookies" element={<CookiesPage />} />
        {/* Served by the API at request time rather than pre-rendered at build time — an article
            exists because a database row says so. See Seo/BlogShellEndpoints.cs. */}
        <Route path="/blog" element={<BlogIndexPage />} />
        <Route path="/en/blog" element={<BlogIndexPage />} />
        <Route path="/blog/tag/:tag" element={<BlogIndexPage />} />
        <Route path="/en/blog/tag/:tag" element={<BlogIndexPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/en/blog/:slug" element={<BlogPostPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/confirm-email" element={<PasswordPages.ConfirmEmail />} />
        <Route path="/forgot-password" element={<PasswordPages.ForgotPassword />} />
        <Route path="/reset-password" element={<PasswordPages.ResetPassword />} />
        {/* Anonymous on purpose: the link is followed from an email, months after the last sign-in. */}
        <Route path="/unsubscribe" element={<UnsubscribePage />} />

        <Route
          path="/onboarding"
          element={
            <RequireAuth>
              <OnboardingPage />
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard"
          element={
            <RequireAuth>
              <DashboardPage />
            </RequireAuth>
          }
        />
        <Route
          path="/income"
          element={
            <RequireAuth>
              <IncomePage />
            </RequireAuth>
          }
        />
        <Route
          path="/stocks"
          element={
            <RequireAuth>
              <StocksPage />
            </RequireAuth>
          }
        />
        <Route
          path="/invoices"
          element={
            <RequireAuth>
              <InvoicesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/vat"
          element={
            <RequireAuth>
              <VatPage />
            </RequireAuth>
          }
        />
        <Route
          path="/calendar"
          element={
            <RequireAuth>
              <CalendarPage />
            </RequireAuth>
          }
        />
        <Route
          path="/declarations"
          element={
            <RequireAuth>
              <DeclarationsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/billing"
          element={
            <RequireAuth>
              <BillingPage />
            </RequireAuth>
          }
        />
        <Route
          path="/settings"
          element={
            <RequireAuth>
              <SettingsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/help"
          element={
            <RequireAuth>
              <HelpPage />
            </RequireAuth>
          }
        />

          <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
    </>
  );
}
